import { distanceInWordsToNow, format, isValid } from "date-fns";
import mailToLink from "mailto-link";
import { Fragment } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import { themeGet } from "styled-system";

import { StyledFontAwesomeIcon } from "pstat-anywhere/components/partials/icons";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { parseDocumentStatusToTranslationKey } from "pstat-anywhere/utils/document";
import titleCase from "pstat-anywhere/utils/titleCase";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import { userDisplay } from "pstat-anywhere/utils/userDisplay";
import { Badge } from "pstat-design-system/Badge";
import { FocusLink } from "pstat-design-system/Link";
import Link from "pstat-design-system/RouterFocusLink";
import { Text } from "pstat-design-system/text";
import Tooltip, {
  HOVER_EVENTS,
  HOVER_EVENTS_OFF
} from "pstat-design-system/Tooltip";

const NullDateMessage = () => {
  const { t } = useTranslation();

  return t("dates.nullDate");
};

export const formatDate = date => {
  // need to check this because effective date can be "Upon Approval", which is not a date

  if (!date) {
    /* 
      new Date(null) returns dec 31, 1969
      So it avoids passing through the date validation
    */
    return <NullDateMessage />;
  }

  if (isValid(new Date(date))) {
    return format(date, "MMMM D[,] YYYY");
  }

  return date;
};

export const formatValueOrEmpty = value => value || "—";

export const formatDateShort = date => {
  if (date) {
    if (isValid(new Date(date))) {
      return format(date, "M/D/YYYY");
    }
  }
  return "—";
};

export const formatDateShortNoDay = date => {
  if (date) {
    if (isValid(new Date(date))) {
      return format(date, "MM/YYYY");
    }
  }
  return "—";
};

export const formatApprovers = (approvers, { rowData }) => {
  return approvers.map(approver => (
    <ApproversListContent approver={approver} rowData={rowData} />
  ));
};

export const formatDateTimeDistance = date => distanceInWordsToNow(date);

const ApproversListContent = withLabelContext(
  ({ labels, approver, rowData }) => {
    const { t } = useTranslation();
    const href = mailToLink({
      to: approver.email,
      body: t("documentReports.awaitingActivation.emailBody", {
        documentLabel: labels.documentLabel,
        documentName: rowData.name,
        waitTime: formatDateTimeDistance(
          rowData.approvalWorkflow.currentStep.created
        ),
        approvalLink: `${window.location.origin}/${getRootUrl()}/policy/${
          rowData.pk
        }`
      })
    });
    return (
      <Link id={`mail-approver-${approver.pk}`} target="_blank" to={href}>
        <StyledFontAwesomeIcon icon={["fal", "envelope"]} color="secondary.0" />
        {approver.lastName} {approver.firstName}
      </Link>
    );
  }
);
const WaitTimeContent = props => (
  <Fragment>
    <StyledFontAwesomeIcon
      data-tip
      data-for={props.tooltipId}
      data-event="mouseover keydown"
      data-event-off="mouseout keyup"
      icon={["fal", "info-circle"]}
      color="secondary.15"
      tabIndex="0"
    />{" "}
    {props.children}
    <Tooltip name={props.tooltipId}>{props.tooltipText}</Tooltip>
  </Fragment>
);

const WaitTimeForLine = withLabelContext(props => {
  const { t } = useTranslation();
  const allAcksAcknowledged =
    props.rowData.totalAcknowledged === props.rowData.totalAssigned;
  const tooltipId = `wait-time-info-${props.rowIndex}`;
  const documentLabel = props.labels.documentLabel.toLowerCase();

  let tooltipText = t("tables.formatters.waitTimeTooltip.default");
  if (allAcksAcknowledged) {
    tooltipText = t("tables.formatters.waitTimeTooltip.allAcksAcknowledged", {
      documentLabel
    });
  }
  return (
    <WaitTimeContent tooltipText={tooltipText} tooltipId={tooltipId}>
      {props.waitTime}
    </WaitTimeContent>
  );
});

export const formatWaitTimeForLine = (waitTime, { rowIndex, rowData }) => (
  <WaitTimeForLine waitTime={waitTime} rowIndex={rowIndex} rowData={rowData} />
);

const WaitTimeForDocument = withLabelContext(props => {
  const { t } = useTranslation();
  const allAcksAcknowledged = props.rowData.dateAcknowledged;
  const tooltipId = `wait-time-info-${props.rowIndex}`;
  const documentLabel = props.labels.documentLabel.toLowerCase();

  let tooltipText = t("tables.formatters.waitTimeTooltip.default");
  if (allAcksAcknowledged) {
    tooltipText = t(
      "tables.formatters.waitTimeForDocumentTooltip.allAcknsAcknowledged",
      { documentLabel }
    );
  }
  return (
    <WaitTimeContent tooltipText={tooltipText} tooltipId={tooltipId}>
      {props.waitTime}
    </WaitTimeContent>
  );
});

export const formatWaitTimeForDocument = (waitTime, { rowIndex, rowData }) => {
  if (waitTime) {
    return (
      <WaitTimeForDocument
        waitTime={waitTime}
        rowIndex={rowIndex}
        rowData={rowData}
      />
    );
  }
  return "—";
};

const AcknowledgedDateContent = props => {
  const date = props.date;
  const iconProps = {};
  let shouldUseTranslation = false;
  let text = date;
  if (date) {
    iconProps.icon = ["fal", "check-circle"];
    iconProps.color = "messages.success";
    if (isValid(new Date(date))) {
      text = format(date, "M/D/YYYY");
    }
  } else {
    shouldUseTranslation = true;
    iconProps.icon = ["fal", "exclamation-triangle"];
    iconProps.color = "messages.error";
    text = "acknowledgments.mine.tabs.outstanding";
  }
  return (
    <Fragment>
      <StyledFontAwesomeIcon {...iconProps} />{" "}
      {shouldUseTranslation ? <Trans i18nKey={text} /> : text}
    </Fragment>
  );
};

export const formatAcknowledgedDateForLine = date => {
  return <AcknowledgedDateContent date={date} />;
};

export const formatAcknowledgedDateForDocument = (date, { rowData }) => {
  if (rowData.dateAssigned) {
    return <AcknowledgedDateContent date={date} />;
  }
  return "—";
};

export const formatDocumentStatus = status => {
  const DocumentTranslatedStatus = ({ status }) => {
    const { t } = useTranslation();
    return t(parseDocumentStatusToTranslationKey(status));
  };

  return <DocumentTranslatedStatus status={status} />;
};

const Preview = styled("span")`
  strong {
    font-weight: ${themeGet("fontWeights.medium")};
  }
`;

export const formatPreview = preview => (
  <Preview dangerouslySetInnerHTML={{ __html: preview }} />
);

export const formatApplicabilityWithHomeIcon = (applicability, { rowData }) => {
  if (rowData.isHomeBloc) {
    return (
      <Fragment>
        {applicability}{" "}
        <StyledFontAwesomeIcon
          icon={["far", "home"]}
          size="lg"
          data-tip
          data-for="home-bloc-tooltip"
          data-delay-show="500"
          color="secondary.0"
        />
        <Tooltip name="home-bloc-tooltip">
          <Trans i18nKey="documentReports.homeBlocTooltip" />
        </Tooltip>
      </Fragment>
    );
  }
  return applicability;
};

export const formatDocumentNameWithAttachmentIcon = (title, { rowData }) => {
  if (rowData.hasAttachments) {
    return (
      <Fragment>
        {title}{" "}
        <StyledFontAwesomeIcon
          icon={["fal", "paperclip"]}
          size="lg"
          color="secondary.0"
        />
      </Fragment>
    );
  }
  return title;
};

export const formatDocumentNameWithLockIcon = (title, { rowData }) => {
  const isRestrictedDefaultPages = rowData.restricted;

  //This condition is used only on "Your Acknowledgment History" report page
  const isRestrictedAcknowledgmentReport = rowData.document?.restricted;

  //This condition is used only on "Saved Drafts" report page
  const isRestrictedDraftPage = rowData.savedCopy?.restricted;

  const isRestricted =
    isRestrictedDefaultPages ||
    isRestrictedAcknowledgmentReport ||
    isRestrictedDraftPage;

  if (isRestricted) {
    return (
      <Fragment>
        {title}{" "}
        <StyledFontAwesomeIcon
          icon={["fas", "lock"]}
          size="lg"
          color="secondary.0"
        />
      </Fragment>
    );
  }
  return title;
};

const DraftType = withLabelContext(({ isEditType, labels }) => {
  const { t } = useTranslation();
  const documentLabel = titleCase(labels.documentLabel);
  if (isEditType) {
    return t("documentDraft.table.draftType.editDraft", { documentLabel });
  }
  return t("documentDraft.table.draftType.newDraft", { documentLabel });
});

export const formatDraftType = isEditType => (
  <DraftType isEditType={isEditType} />
);

export const formatDraftSaver = saverDisplay => {
  if (saverDisplay !== "") {
    return saverDisplay;
  }
  return <Trans i18nKey="documentDraft.table.creator.you" />;
};

const EditDocumentLink = ({ to, children, index }) => (
  <Fragment>
    <Link
      id={`edit-action-${index}`}
      to={to}
      data-tip
      data-for={`edit-icon-tooltip-${index}`}
      data-event={HOVER_EVENTS}
      data-event-off={HOVER_EVENTS_OFF}
    >
      {children}
      <StyledFontAwesomeIcon icon={["fas", "edit"]} size="lg" mx={2} />
    </Link>
    <Tooltip name={`edit-icon-tooltip-${index}`}>
      <Trans i18nKey="documentControl.view.secondaryNav.navOptions.edit.name" />
    </Tooltip>
  </Fragment>
);

export const editLink = (value, { rowData }) => {
  const [documentToEditPk, pagePath] = rowData.document
    ? [rowData.document.pk, "edit"]
    : [rowData.savedCopy.pk, "create"];
  return (
    <EditDocumentLink
      to={`${getRootUrl()}/policy/${documentToEditPk}/${pagePath}`}
      index={rowData._index}
    >
      {value}
    </EditDocumentLink>
  );
};

const Container = styled.span`
  display: inline-flex;
  alignitems: center;
  cursor: pointer;
`;

const DeleteDraftButton = ({ children, onClick, index }) => {
  const handleKeyDown = event => {
    const isEnterKey = event => event.key === "Enter";
    if (isEnterKey(event)) {
      onClick(event);
    }
  };
  return (
    <Fragment>
      {children}
      <Container
        id={`delete-action-${index}`}
        data-tip
        data-for={`delete-icon-tooltip-${index}`}
        data-event={HOVER_EVENTS}
        data-event-off={HOVER_EVENTS_OFF}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex="0"
      >
        <StyledFontAwesomeIcon icon={["fas", "trash-alt"]} size="lg" mx={2} />
        <Tooltip name={`delete-icon-tooltip-${index}`}>Delete</Tooltip>
      </Container>
    </Fragment>
  );
};

export const deleteButton = (value, { rowData }) => (
  <DeleteDraftButton
    onClick={() => rowData.openDiscardModal(rowData.pk)}
    index={rowData._index}
  >
    {value}
  </DeleteDraftButton>
);

export const formatAuthor = (value, { rowData }) => userDisplay(value);

export const formatImportFile = (file, { rowIndex }) => (
  <Fragment>
    <FocusLink href={file.url}>{file.name}</FocusLink>
    {file.zipName && (
      <Fragment>
        <FocusLink
          href={file.zipUrl}
          data-tip
          data-for={`zip-file-tooltip-${rowIndex}`}
          data-event={HOVER_EVENTS}
          data-event-off={HOVER_EVENTS_OFF}
        >
          <StyledFontAwesomeIcon icon={["far", "file-archive"]} mx={0} />
        </FocusLink>
        <Tooltip name={`zip-file-tooltip-${rowIndex}`}>{file.zipName}</Tooltip>
      </Fragment>
    )}
  </Fragment>
);

export const formatBlocSites = (tenants, { rowIndex }) => {
  const firstXTenants = 3;
  const firstThreeTenants = tenants.slice(0, firstXTenants);
  const moreThanThree = tenants.length > firstXTenants;
  return (
    <Fragment>
      {firstThreeTenants.map(tenant => {
        return (
          <Badge color="nav.25" fontWeight="normal" ml={0} mr={1}>
            {tenant.subdomain}
          </Badge>
        );
      })}
      {moreThanThree && (
        <Text fontSize={1} color="nav.25">
          + {tenants.length - firstXTenants} more
        </Text>
      )}
    </Fragment>
  );
};

const BlocEditIcon = ({ icon, isEditable, rowIndex }) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <StyledFontAwesomeIcon
        id={`edit-icon-${rowIndex}`}
        data-tip
        data-for={`edit-icon-tooltip-${rowIndex}`}
        data-event={HOVER_EVENTS}
        data-event-off={HOVER_EVENTS_OFF}
        icon={icon}
        size="xl"
        color={isEditable ? "secondary.0" : "nav.55"}
        mx={isEditable ? 1 : 2}
      />
      <Tooltip name={`edit-icon-tooltip-${rowIndex}`}>
        {isEditable ? (
          <Fragment>
            {t("documentControl.view.secondaryNav.navOptions.edit.name")}
          </Fragment>
        ) : (
          <Fragment>{t("bloc.manage.cannotEditTooltip")}</Fragment>
        )}
      </Tooltip>
    </Fragment>
  );
};

const BlocIconLink = ({ isEditable, rowIndex, pk }) => {
  const { t } = useTranslation();
  return (
    <Link
      to={`${getRootUrl()}/bloc/${pk}/edit`}
      aria-label={t("bloc.manage.linkAriaLabel")}
    >
      <BlocEditIcon
        icon={["fas", "edit"]}
        isEditable={isEditable}
        rowIndex={rowIndex}
      />
    </Link>
  );
};

export const formatBlocEditIcon = (isEditable, { rowIndex, rowData }) => {
  if (isEditable) {
    return (
      <BlocIconLink
        isEditable={isEditable}
        rowIndex={rowIndex}
        pk={rowData.pk}
      />
    );
  } else {
    return (
      <BlocEditIcon
        icon={["fas", "edit"]}
        isEditable={isEditable}
        rowIndex={rowIndex}
      />
    );
  }
};

export const formatBlocTrashIcon = blocIsEditable => (
  isEditable,
  { rowIndex }
) => {
  return (
    <BlocEditIcon
      icon={["fas", "trash-alt"]}
      isEditable={blocIsEditable}
      rowIndex={rowIndex}
    />
  );
};

const ViewDuplicatesLabel = () => {
  const { t } = useTranslation();
  return t("documentReports.duplicates.viewDuplicates");
};
export const formatViewDuplicates = () => <ViewDuplicatesLabel />;
